<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable"></report-tools>

    <report-state
      v-if="!selectedCompany"
      icon="mdi-domain"
      :title="$t('report_mobile.employees_pick_company')"
    ></report-state>
    <report-state v-else-if="loading" kind="loading"></report-state>
    <report-state
      v-else-if="!rows.length"
      icon="mdi-account-cash-outline"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>

    <div v-else class="rd-card rd-card--scroll">
      <table class="rd-tbl rd-tbl--workers">
        <thead>
          <tr>
            <th scope="col" class="rd-col-id" :aria-sort="ariaSort('id')">
              <button type="button" @click="sortBy('id')">ID {{ sortArrow("id") }}</button>
            </th>
            <th scope="col" :aria-sort="ariaSort('name')">
              <button type="button" @click="sortBy('name')">{{ $t("name") }} {{ sortArrow("name") }}</button>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('ordersCount')">
              <button type="button" @click="sortBy('ordersCount')">{{ $t("orders") }} {{ sortArrow("ordersCount") }}</button>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('ordersTotal')">
              <button type="button" @click="sortBy('ordersTotal')">
                {{ $t("orders_total") }}{{ currencySuffix }} {{ sortArrow("ordersTotal") }}
              </button>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('spent')">
              <button type="button" @click="sortBy('spent')">
                {{ $t("spent_on_credits") }}{{ currencySuffix }} {{ sortArrow("spent") }}
              </button>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('balance')">
              <button type="button" @click="sortBy('balance')">
                {{ $t("credit_balance") }}{{ currencySuffix }} {{ sortArrow("balance") }}
              </button>
            </th>
            <th scope="col" class="rd-col-go"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="row.key"
            data-open
            tabindex="0"
            :class="{ 'is-selected': isOpen(row) }"
            @click="openUser(row, $event)"
            @keydown.enter.prevent="openUser(row, $event)"
            @keydown.space.prevent="openUser(row, $event)"
          >
            <td class="rd-col-id rd-num rd-muted">{{ row.id }}</td>
            <td class="is-name" :title="row.name">
              <span class="rd-person">
                <span class="rd-avatar rd-avatar--sm">{{ row.initials }}</span>
                <span class="rd-person__name">{{ row.name }}</span>
              </span>
            </td>
            <td class="is-right rd-num" :class="{ 'is-zero': !row.ordersCount }">{{ row.ordersCount }}</td>
            <td class="is-right rd-num" :class="{ 'is-zero': !row.ordersTotal }">{{ row.ordersTotal }}</td>
            <td class="is-right rd-num" :class="{ 'is-zero': !row.spent }">{{ row.spent }}</td>
            <td class="is-right rd-num" :class="{ 'is-alert': row.balance < 0 }">{{ row.balance }}</td>
            <td class="rd-col-go"><v-icon size="18" color="#8a817a">mdi-chevron-right</v-icon></td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td class="rd-col-id"></td>
            <td>{{ $t("total") }}</td>
            <td class="is-right rd-num">{{ sums.ordersCount }}</td>
            <td class="is-right rd-num">{{ sums.ordersTotal }}</td>
            <td class="is-right rd-num">{{ sums.spent }}</td>
            <td class="is-right rd-num">{{ sums.balance }}</td>
            <td class="rd-col-go"></td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import ReportCreditTabUsers from "@/components/report/more-restaurants/tabs/ReportCreditTabUsers.vue";
import { copyText, rowsToTsv } from "../../copyTable";
import hasMoreCreditRows from "../../../shared/more/hasMoreCreditRows";
import hasTableSort from "../../hasTableSort";
import ReportState from "../../ReportState.vue";
import ReportTools from "../../ReportTools.vue";
import ReportDesktopCreditUser from "./ReportDesktopCreditUser.vue";

/**
 * Kreditni korisnici (firme "više restorana") na računaru: koliko su korisnici naručili, potrošili na kredit i
 * koliko im je ostalo. Klik na red otvara narudžbe tog korisnika po mesecima u panelu. Firmu i period čita iz
 * zajedničke trake, pretragu (po imenu ili ID-u) takođe. Nasleđuje učitavanje, štampu i Excel iz starog taba.
 */
export default {
  name: "ReportDesktopMoreCredit",
  extends: ReportCreditTabUsers,
  mixins: [hasTableSort, hasMoreCreditRows],
  components: { ReportState, ReportTools },
  inject: {
    shell: { from: "reportShell", default: null },
  },
  data: () => ({
    sort: { key: "name", dir: 1 },
  }),
  computed: {
    rows() {
      return this.sortRows(this.matchedRows);
    },
    emptyText() {
      return this.filters.search ? "" : this.$t("select_date_and_filters");
    },
    kpis() {
      return [
        { key: "users", label: this.$t("users"), value: this.rows.length, tone: "inverse" },
        { key: "spent", label: this.$t("spent_on_credits"), value: this.sums.spent },
        { key: "balance", label: this.$t("credit_balance"), value: this.sums.balance },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.rows.length ? kpis : []);
      },
      immediate: true,
    },
    loading: {
      handler(loading) {
        this.$emit("loading", loading);
      },
      immediate: true,
    },
  },
  methods: {
    isOpen(row) {
      const top = this.shell?.panelStack?.[0];
      return Boolean(top && top.key === `credit-${row.id}`);
    },
    /** Narudžbe korisnika po mesecima u panelu (isti podaci kao stari dijalog: /credits/report). */
    openUser(row, event) {
      const company = this.selectedCompany;
      this.$emit("open-panel", {
        opener: event?.currentTarget,
        key: `credit-${row.id}`,
        root: true,
        wide: true,
        title: row.name,
        subtitle: [`ID ${row.id}`, this.toLatin((company && (company.name || company.company)) || "")].filter(Boolean).join(" · "),
        component: ReportDesktopCreditUser,
        props: { user: row.source, companyId: company && company.id },
      });
    },
    async copyTable() {
      const headers = [
        "ID",
        this.$t("name"),
        this.$t("orders"),
        this.$t("orders_total"),
        this.$t("spent_on_credits"),
        this.$t("credit_balance"),
      ];
      const rows = this.rows.map((row) => [row.id, row.name, row.ordersCount, row.ordersTotal, row.spent, row.balance]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
